import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useState, useImperativeHandle, memo } from 'react';
import classNames from 'classnames';
import Transition from '@helper/transition/Transition';
import PortalContainer from '@widget/popups/PortalContainer';
const SlideInPanelBase = React.forwardRef((props, ref) => {
    const { children, isOpen, onClose, mode = 'full', direction = 'right', width, height, className, wrapperClassName, unmountOnExit = true, containerRef, borderRadius, } = props;
    const [containerBounds, setContainerBounds] = useState(null);
    // Expose handlers to parent
    useImperativeHandle(ref, () => ({
        close: () => {
            onClose();
        },
    }));
    useEffect(() => {
        // If the consumer isn't in full + containerRef mode, drop the bounds.
        if (mode !== 'full' || !(containerRef === null || containerRef === void 0 ? void 0 : containerRef.current)) {
            setContainerBounds(null);
            return;
        }
        // While closing (isOpen=false) keep the last-known bounds so the exit
        // animation continues to play from the same position. Clearing them
        // here would re-class the wrapper to `--default` mid-animation and
        // the panel would slide from the wrong place.
        if (!isOpen) {
            return;
        }
        const updateBounds = () => {
            if (containerRef.current) {
                setContainerBounds(containerRef.current.getBoundingClientRect());
            }
        };
        updateBounds();
        const resizeObserver = new ResizeObserver(updateBounds);
        resizeObserver.observe(containerRef.current);
        window.addEventListener('resize', updateBounds);
        window.addEventListener('scroll', updateBounds);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateBounds);
            window.removeEventListener('scroll', updateBounds);
        };
    }, [containerRef, isOpen, mode]);
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);
    const isModal = mode === 'modal';
    const animationClassName = isModal
        ? 'uxpcore_slide_panel__animation_modal'
        : `uxpcore_slide_panel__animation_${direction}`;
    // Full mode: wrapper fills the content area or the containerRef bounds
    const fullWrapperStyle = containerBounds ? {
        position: 'fixed',
        top: containerBounds.top,
        left: containerBounds.left,
        width: containerBounds.width,
        height: containerBounds.height,
        zIndex: 999,
    } : {};
    // Modal mode: explicit width/height if provided
    const modalWrapperStyle = Object.assign(Object.assign({}, (width ? { width } : {})), (height ? { height } : {}));
    // Optional border-radius override. Set both `borderRadius` and the
    // logical start-start property because the SCSS sets the latter
    // explicitly — without overriding it the corner would still inherit
    // the SCSS default.
    const radiusStyle = borderRadius !== undefined
        ? { borderRadius, borderStartStartRadius: borderRadius }
        : {};
    const wrapperStyle = Object.assign(Object.assign({}, (isModal ? modalWrapperStyle : fullWrapperStyle)), radiusStyle);
    const wrapperClass = classNames('uxpcore_slide_panel__wrapper', isModal
        ? 'uxpcore_slide_panel__wrapper--modal'
        : {
            'uxpcore_slide_panel__wrapper--positioned': !!containerBounds,
            'uxpcore_slide_panel__wrapper--default': !containerBounds,
        }, wrapperClassName);
    const backdropClass = classNames('uxpcore_slide_panel__backdrop', { 'uxpcore_slide_panel__backdrop--modal': isModal });
    return (_jsx(Transition, { in: isOpen, duration: 300, className: animationClassName, unmountOnExit: unmountOnExit, children: _jsx(PortalContainer, { hasBackdrop: true, onClickBackdrop: onClose, className: backdropClass, children: _jsx("div", { className: wrapperClass, style: wrapperStyle, children: _jsx("div", { className: classNames("uxpcore_slide_panel__content", className), children: children }) }) }) }));
});
/**
 * @export
 * A component that renders a slide-in panel with animated transitions and a backdrop.
 * Supports two modes:
 * - `full`: fills the main content area (default)
 * - `modal`: centered overlay, auto-sized to content
 *
 * @example
 * ```
 * // Full mode (default)
 * <SlideInPanel isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <div>Panel Content</div>
 * </SlideInPanel>
 * ```
 *
 * @example
 * ```
 * // Modal mode — auto-sized
 * <SlideInPanel mode="modal" isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <div>Modal Content</div>
 * </SlideInPanel>
 * ```
 *
 * @example
 * ```
 * // Modal mode — explicit size
 * <SlideInPanel mode="modal" width="500px" height="400px" isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <div>Modal Content</div>
 * </SlideInPanel>
 * ```
 *
 * @example
 * ```
 * // Full mode with containerRef
 * const containerRef = useRef<HTMLDivElement>(null);
 * const panelRef = useRef<SlideInPanelHandlers>(null);
 *
 * <div ref={containerRef} style={{ width: 400, height: 300 }}>
 *   Container content
 * </div>
 *
 * <SlideInPanel
 *   isOpen={true}
 *   onClose={() => console.log('Closed')}
 *   containerRef={containerRef}
 *   ref={panelRef}
 * >
 *   <div>Panel Content</div>
 * </SlideInPanel>
 * ```
 */
export const SlideInPanel = memo(SlideInPanelBase);
